import { useEffect, useRef, useState } from "react";
import { HEARTBEAT_INTERVAL_MS } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { mergeObservedPresence } from "../utils/presence";

// #2366 — polls GET /presence (#2363) for the CURRENT class only ("while a
// ride is up" — AC1), same visibility-aware fetch/poll shape as
// useClassResults.js: pause while the tab is hidden, refresh immediately on
// return, never fetch at all with no classId.
//
// Reset happens on classId change, not on every poll: the "observed this
// session" map (utils/presence.js's file header) is deliberately scoped to
// the class currently up, so a seat that went quiet on a class that ended
// earlier does not keep reading red once the ring has moved on to a
// DIFFERENT class's judges. A ring switch (different `ring`) always comes
// with a different `classId` too (a class belongs to one ring), so keying
// the reset on classId alone covers both.
//
// #2190/additive-only doctrine: a failed poll changes nothing. This is
// PURELY informational — there is no "loading"/"error" state exposed at
// all, because there is nothing for a caller to degrade: an empty map
// renders no presence chips, identical to today's screen before this card.
export function useObservedPresence(showId, ring, classId) {
  const api = useScoreApi();
  const [observed, setObserved] = useState({});
  const classIdRef = useRef(classId);
  const apiRef = useRef(api);
  apiRef.current = api;

  useEffect(() => {
    // A genuinely new class starts with a clean slate — see file header.
    if (classIdRef.current !== classId) {
      classIdRef.current = classId;
      setObserved({});
    }
  }, [classId]);

  useEffect(() => {
    if (!showId || !ring || !classId) return undefined;

    const poll = async () => {
      try {
        const entries =
          unwrap(await apiRef.current.presence.listPresence(showId, ring, classId)) ?? [];
        // A class switch mid-flight must not let a stale response for the
        // OLD classId land in the NEW class's map.
        if (classIdRef.current !== classId) return;
        setObserved((prev) => mergeObservedPresence(prev, entries));
      } catch {
        // Additive-only (card rule): presence is informational, never an
        // error state. Leave the observed map exactly as it is.
      }
    };

    if (document.visibilityState !== "hidden") poll();
    let timer = setInterval(() => {
      if (document.visibilityState !== "hidden") poll();
    }, HEARTBEAT_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState !== "hidden") poll();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(timer);
      timer = null;
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [showId, ring, classId]);

  return observed;
}

export default useObservedPresence;
